
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';

@Component({
  selector: 'app-transport-asset-details-modal',
  standalone: false,
  templateUrl: './transport-asset-details-modal.component.html',
  styleUrl: './transport-asset-details-modal.component.scss'
})
export class TransportAssetDetailsModalComponent {
  form!: FormGroup;
  isSubmitting: boolean = false;
  isSubmitAttempted: boolean = false;
  transportsList: any[];
  transportRoutesList: any[];
  isLoading: boolean = false;

  showErrorMessages: boolean = false;


  lgaList: any;
  zoneList: any;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    public dialogRef: MatDialogRef<TransportAssetDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    private utilitiesService: UtilitiesService
  ) { }

  ngOnInit(): void {
    this.initializeForm();

    this.fetchTransports();

    this.subscribeToTransportChange()

    if(this.dialogData) {
      this.patchForm(this.dialogData)
    }

  }

  initializeForm() {
    this.form = this.formBuilder.group({
      plateNumber: [null, [Validators.required]],
      route: [ null, [Validators.required,],],
      transportRoute: [null, [Validators.required]],
    });

  }

  patchForm(details) {
    this.form.patchValue({
      plateNumber: details.plateNumber,
      route: details.transportRouteId,
      transportRoute: details.routeId,
    })
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const cleanedPhoneNumber = control.value
        .replace(/\s/g, '')
        .replace(/-/g, '');

      const isDigitsOnly = /^\d+$/.test(cleanedPhoneNumber);

      if (
        cleanedPhoneNumber.length === 0 ||
        (cleanedPhoneNumber.length === 11 && isDigitsOnly) ||
        (cleanedPhoneNumber.length === 13 &&
          cleanedPhoneNumber.startsWith('234')) ||
        (cleanedPhoneNumber.length === 14 &&
          cleanedPhoneNumber.startsWith('+234'))
      ) {
        return null;
      } else {
        return { invalidPhoneNumber: true };
      }
    };
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

      if (control.value && !emailPattern.test(control.value)) {
        return { invalidEmail: true };
      }

      return null;
    };
  }

  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,

      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {

      this.dialogRef.close({reload: true})
    });
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => { });
  }

  openConfirmModal() {
    this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'operation-modal', 'modal-no-overflow-changed'],
      disableClose: true,

      data: {
        title: 'Are you sure you want to edit these details?',
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {
          this.submit();
        },
        onCancel: () => { },
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }

  async submit(): Promise<void> {
    if (this.form.valid) {
      this.isSubmitting = true;

      try {
        const formData = this.form.value;
        const request = await this.mapFormValuesToObject(formData);

        const url = '/transport/update/asset-detail';

        this.apiService
          .post(url, request)
          .pipe(finalize(() => (this.isSubmitting = false)))
          .subscribe(
            (res) => {
              this.openSuccessModal(res.message || 'Asset details updated successfully');
            },
            (error) => {
              this.openErrorModal(error?.message);
            }
          );
      } catch (error) {
        this.isSubmitting = false;
        this.openErrorModal('Failed to process form data');
      }
    }
  }

  async mapFormValuesToObject(formValue: any): Promise<any> {
    return {
      id: this.dialogData.id,
      plateNumber: formValue.plateNumber,
      route: formValue.route,
      transportRoute: formValue.transportRoute
    };
  }

  fetchTransports() {
    this.isLoading = true;
    this.apiService
      .get('/transport/all/transport')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.transportsList = response.map((item) => ({
          ...item,
          formattedName: this.toTitleCase(item.name.replace(/_/g, ' ')),
        }));
      });
  }

  get routeControl () {
    return this.form.get('route') as FormControl;
  }

  get transportRouteControl () {
    return this.form.get('transportRoute') as FormControl;
  }

  subscribeToTransportChange() {

    this.routeControl.valueChanges.subscribe(transportId => {
      this.fetchTransportRoutes(transportId);
      this.transportRouteControl.reset()
    })
  }

  fetchTransportRoutes(transportId: number) {
    this.isLoading = true;
    this.apiService
      .get('/transport/all/transport/route/' + transportId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.transportRoutesList = response.map((item) => ({
          ...item,
          formattedName: this.toTitleCase(item.name.replace(/_/g, ' ')),
        }));
      });
  }



  get lgaControl() {
    return this.form.get('lga') as FormControl
  }

  get zoneControl() {
    return this.form.get('zone') as FormControl
  }


  loadLgaList() {
    this.utilitiesService.getLocalGovernmentList().subscribe(
      (response) => {
        this.lgaList = response;
      },
      (error) => {
        this.openErrorModal('Error Fetching Local Government');
      }
    );
  }

  subscribeToLgaChanges() {
    this.lgaControl.valueChanges.subscribe(lga => {
      this.zoneList = null;
      this.zoneControl.reset()
      this.getZonesFromLga(lga);
    })
  }

  getZonesFromLga(lga) {
    this.utilitiesService.getZonesFromLga(lga).subscribe((res: any) => {
      this.zoneList = res;
    });
  }


  toggleSelection(item: any, selected: boolean, event: Event) {
    event.stopPropagation();

    setTimeout(() => {
      const control = this.form.get('park');
      let selectedValues = [...(control?.value || [])];

      if (selected) {
        selectedValues = selectedValues.filter((id: any) => id !== item.id);
      } else {
        selectedValues.push(item.id);
      }

      control?.setValue(selectedValues);
      control?.updateValueAndValidity();
    });
  }

  toTitleCase(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  closeModal(): void {
    this.form.reset();
    this.dialogRef.close();
  }
}
